import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare,
  Plus,
  Trash2,
  Edit,
  Users,
  Video,
  Calendar,
  Clock,
  Play,
  Pause,
  ExternalLink,
  RefreshCw,
  Radio
} from 'lucide-react';

const AdminTownHall = () => {
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduled_time: '',
    stream_url: '',
    stream_platform: '',
    is_active: false
  });

  const fetchSessions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/townhall/sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a session title');
      return;
    }

    try {
      if (editingSession) {
        await axios.patch(`${API}/admin/townhall/sessions/${editingSession.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Session updated');
      } else {
        await axios.post(`${API}/admin/townhall/sessions`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Town Hall session created');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchSessions();
    } catch (error) {
      toast.error('Failed to save session');
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this Town Hall session?')) return;
    
    try {
      await axios.delete(`${API}/admin/townhall/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session deleted');
      fetchSessions();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const toggleSessionActive = async (session) => {
    try {
      await axios.patch(`${API}/admin/townhall/sessions/${session.id}`, {
        is_active: !session.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(session.is_active ? 'Session ended' : 'Session is now live!');
      fetchSessions();
    } catch (error) {
      toast.error('Failed to update session');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      scheduled_time: '',
      stream_url: '',
      stream_platform: '',
      is_active: false
    });
    setEditingSession(null);
  };

  const openEditDialog = (session) => {
    setEditingSession(session);
    setFormData({
      title: session.title || '',
      description: session.description || '',
      scheduled_time: session.scheduled_time ? session.scheduled_time.slice(0, 16) : '',
      stream_url: session.stream_url || '',
      stream_platform: session.stream_platform || '',
      is_active: session.is_active || false
    });
    setDialogOpen(true);
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'youtube': return '🔴';
      case 'facebook': return '📘';
      case 'instagram': return '📷';
      default: return '🎥';
    }
  };

  const activeSessions = sessions.filter(s => s.is_active);
  const upcomingSessions = sessions.filter(s => !s.is_active && s.scheduled_time && new Date(s.scheduled_time) > new Date());
  const pastSessions = sessions.filter(s => !s.is_active && (!s.scheduled_time || new Date(s.scheduled_time) <= new Date()));

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-4 border-plp-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-plp-gold" />
            Town Hall Sessions
          </h1>
          <p className="text-slate-600 mt-1">Create live chat sessions where the community can interact with Robyn</p>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchSessions}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-plp-gold hover:bg-plp-gold/90 text-plp-blue" data-testid="create-session-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Session
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingSession ? 'Edit Town Hall Session' : 'Create Town Hall Session'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Session Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Community Q&A with Robyn"
                    data-testid="session-title-input"
                  />
                </div>
                
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What will be discussed in this session?"
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label>Scheduled Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                  />
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    Live Stream Link (Optional)
                  </h4>
                  <p className="text-xs text-slate-500 mb-3">
                    Add a YouTube, Facebook, or Instagram live stream link to display alongside the chat
                  </p>
                  
                  <div className="space-y-3">
                    <div>
                      <Label>Stream Platform</Label>
                      <Select 
                        value={formData.stream_platform} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, stream_platform: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select platform (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="youtube">🔴 YouTube</SelectItem>
                          <SelectItem value="facebook">📘 Facebook</SelectItem>
                          <SelectItem value="instagram">📷 Instagram</SelectItem>
                          <SelectItem value="other">🎥 Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Stream URL</Label>
                      <Input
                        value={formData.stream_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, stream_url: e.target.value }))}
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-2">
                  <div>
                    <Label>Start Session Immediately</Label>
                    <p className="text-xs text-slate-500">Make this session live now</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                </div>
                
                <Button type="submit" className="w-full bg-plp-blue hover:bg-plp-blue/90" data-testid="save-session-btn">
                  {editingSession ? 'Update Session' : 'Create Session'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading text-lg font-bold text-emerald-600 flex items-center gap-2">
            <Radio className="w-5 h-5 animate-pulse" />
            Live Now
          </h2>
          <div className="grid gap-4">
            {activeSessions.map((session) => (
              <Card key={session.id} className="border-2 border-emerald-500 bg-emerald-50">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-emerald-500 text-white animate-pulse">
                          <Radio className="w-3 h-3 mr-1" /> LIVE
                        </Badge>
                        {session.stream_platform && (
                          <span className="text-lg">{getPlatformIcon(session.stream_platform)}</span>
                        )}
                      </div>
                      <h3 className="font-heading text-xl font-bold text-slate-800">{session.title}</h3>
                      {session.description && (
                        <p className="text-slate-600 mt-1">{session.description}</p>
                      )}
                      {session.stream_url && (
                        <a 
                          href={session.stream_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-plp-blue hover:underline flex items-center gap-1 mt-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View Live Stream
                        </a>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {session.participant_count || 0} participants
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {session.message_count || 0} messages
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => toggleSessionActive(session)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Pause className="w-4 h-4 mr-2" />
                        End Session
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Sessions */}
      {upcomingSessions.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading text-lg font-bold text-slate-700 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Upcoming Sessions
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {upcomingSessions.map((session) => (
              <Card key={session.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">
                          <Clock className="w-3 h-3 mr-1" />
                          {new Date(session.scheduled_time).toLocaleString()}
                        </Badge>
                        {session.stream_platform && (
                          <span>{getPlatformIcon(session.stream_platform)}</span>
                        )}
                      </div>
                      <h3 className="font-heading font-bold text-slate-800">{session.title}</h3>
                      {session.description && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{session.description}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleSessionActive(session)}
                        className="text-emerald-500 hover:bg-emerald-50"
                        title="Start Session"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(session)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(session.id)}
                        className="text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Past Sessions */}
      {pastSessions.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading text-lg font-bold text-slate-500 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Past Sessions
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pastSessions.map((session) => (
              <Card key={session.id} className="opacity-75">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-slate-700">{session.title}</h3>
                      {session.scheduled_time && (
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(session.scheduled_time).toLocaleString()}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                        <span>{session.message_count || 0} messages</span>
                        <span>{session.participant_count || 0} participants</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(session.id)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {sessions.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <MessageSquare className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No Town Hall sessions yet</h3>
            <p className="text-slate-500 mt-1">Create your first session to start engaging with the community</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminTownHall;
